import { authHeaders, parseError } from '../services/campaignAdminApi';
import { clearCacheByPrefix } from '../ui/listCache';

const apiUrl = process.env.REACT_APP_API_URL;

/** The only audience this screen can grant — the caller's own organization. */
export const ORG_CATALOG_AUDIENCE = 'enterprise';

/**
 * Parses GET /api/org/course-catalog. Missing fields become empty rather than
 * throwing, so a half-loaded response still renders instead of blanking the page.
 */
export function parseOrgCatalogResponse(data) {
  return {
    orgId: data?.orgId || '',
    courses: Array.isArray(data?.courses) ? data.courses : [],
  };
}

/** Courses not yet in the catalog — the Add dropdown's options. */
export function coursesAvailableToAdd(courses) {
  return (courses || []).filter((course) => course && !course.shared);
}

/** Courses currently shared with the organization's instructors. */
export function catalogEntries(courses) {
  return (courses || []).filter((course) => course && course.shared);
}

/**
 * Optimistic update used by both Copy and Remove. Returns a new array; the
 * original is left alone so a failed request can roll back by restoring it.
 */
export function applySharingLocally(courses, courseIds, shared) {
  const idSet = new Set(courseIds || []);
  const now = String(Date.now());
  return (courses || []).map((course) => {
    if (!idSet.has(course.courseId)) {
      return course;
    }
    return {
      ...course,
      shared: Boolean(shared),
      sharedAt: shared ? (course.sharedAt || now) : course.sharedAt,
    };
  });
}

/**
 * `sharedAt` is epoch millis stored as a string. Anything unparseable reads as
 * an em dash rather than "Invalid Date", which would look like a broken row.
 */
export function formatSharedAt(value) {
  if (value == null || value === '') {
    return '—';
  }
  const numeric = Number(value);
  const date = Number.isFinite(numeric) ? new Date(numeric) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export async function fetchOrgCourseCatalog({ adminUserId, orgId }) {
  const params = new URLSearchParams();
  params.set('adminUserId', adminUserId || '');
  if (orgId) {
    params.set('orgId', orgId);
  }
  const response = await fetch(`${apiUrl}/api/org/course-catalog?${params.toString()}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  if (!response.ok) {
    throw await parseError(response, `Could not load the course catalog: ${response.status}`);
  }
  return parseOrgCatalogResponse(await response.json());
}

export async function updateOrgCourseSharing({ adminUserId, orgId, courseIds, shared }) {
  const response = await fetch(`${apiUrl}/api/org/course-catalog`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({
      adminUserId,
      orgId,
      courseIds,
      shared,
    }),
  });
  if (!response.ok) {
    throw await parseError(response, `Could not update the course catalog: ${response.status}`);
  }
  return response.json();
}

/**
 * The instructor catalog is cached per user and org. Sharing or withdrawing a
 * course must drop that cache on this browser, otherwise the admin would go
 * check the catalog and still see the previous list.
 */
export function invalidateInstructorCatalogCache() {
  clearCacheByPrefix('catalog:');
}
